import {
  DownloadOutlined,
  ExportOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  InputNumber,
  Popover,
  Space,
  Table,
  TableColumnType,
  Upload,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import ModalPreviewExport from "../../components/modal/Warehouse/ModalPreviewExport";
import {
  IPartNumberDtoExport,
  IPartNumberDtoResponse,
} from "../../models/Warehouse";
import {
  useImportWarehouseExport,
  useWarehouses,
} from "../../query/WarehouseQuery";
import { RootState } from "../../store";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";

const WarehouseExport: React.FC = () => {
  const [searchParams, setSearchParams] = useState<{
    awb?: string;
    partNumber?: string;
  }>({});
  const warehouse = useSelector(
    (state: RootState) => state.warehouse.locations,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>();
  const importMutation = useImportWarehouseExport();
  const { data: warehouseData, isLoading } = useWarehouses({
    page: currentPage,
    limit: pageSize,
    ...searchParams,
    code: warehouseFilter,
  });
  const filterOptions = warehouse
    .filter((item) => item.warehouseCode !== "QC")
    .map((item) => ({
      label: item.warehouseCode,
      value: item.warehouseCode,
    }));
  const [selectedItems, setSelectedItems] = useState<IPartNumberDtoExport[]>(
    [],
  );
  const [previewVisible, setPreviewVisible] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(
    debounce((field: string, value: string) => {
      setSearchParams((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
      setCurrentPage(1);
    }, 300),
    [],
  );

  const handleExcelUpload = async (file: File) => {
    await importMutation.mutateAsync(file, {
      onSuccess: (data) => {
        if (data && Array.isArray(data)) {
          const processedItems = data.map((item) => ({
            ...item,
            exportQuantity: item.exportQuantity || 1,
          }));

          setSelectedItems(processedItems);
          setPreviewVisible(true);
          notify.success(
            `Đã xử lý ${processedItems.length} mã hàng từ file Excel`,
          );
        } else {
          notify.error("Định dạng dữ liệu không hợp lệ");
        }
      },
      onError: (error: any) => {
        if (error.isAxiosError) {
          const serverMessage = error.response?.data?.message;
          notify.error(
            serverMessage || "Có lỗi khi xử lý file. Vui lòng thử lại.",
          );
        } else {
          notify.error("Kiểm tra lại thông tin dữ liệu xuất kho và thử lại");
        }
      },
    });
    return false;
  };

  const handlePreview = () => {
    if (selectedItems.length > 0) {
      setPreviewVisible(true);
    }
  };

  const handleExportSubmit = async () => {
    try {
      setPreviewVisible(false);
      setSelectedItems([]);
    } catch (error) {}
  };

  const columns: TableColumnType<IPartNumberDtoResponse>[] = [
    // {
    //   title: "Ngày",
    //   dataIndex: "importDate",
    //   key: "date",
    //   width: 100,
    //   minWidth: 100,
    //   align: "center",
    // },
    {
      title: "AWB",
      dataIndex: ["awb"],
      key: "awb",
      width: 100,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm AWB"
              onChange={(e) => {
                handleSearchChange("awb", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.awb ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã kho",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      key: "warehouseCode",
      width: 100,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: "8px" }}>
            <Space direction="vertical">
              <Space wrap>
                {filterOptions.map((option) => (
                  <Button
                    key={option.value}
                    type={
                      warehouseFilter === option.value ? "primary" : "default"
                    }
                    onClick={() => {
                      setWarehouseFilter(
                        warehouseFilter === option.value
                          ? undefined
                          : option.value,
                      );
                      setCurrentPage(1);
                    }}
                    size="small"
                  >
                    {option.label}
                  </Button>
                ))}
              </Space>
              {warehouseFilter && (
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={() => {
                    setWarehouseFilter(undefined);
                    setCurrentPage(1);
                  }}
                >
                  Xóa bộ lọc
                </Button>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: "Dự án",
      dataIndex: "project",
      key: "project",
      width: 80,
      align: "center",
    },
    {
      title: "Mã hàng",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 120,
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm mã hàng"
              onChange={(e) => {
                handleSearchChange("partNumber", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: searchParams.partNumber ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      width: 110,
    },
    {
      title: "QC Status",
      dataIndex: "qcStatus",
      key: "qcStatus",
      width: 120,
      render: (status: string) => renderQCStatusTag(status),
    },
    {
      title: "Số lượng",
      dataIndex: "stock",
      key: "stock",
      width: 100,
    },
    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
      width: 100,
    },
    {
      title: "Location",
      dataIndex: ["location", "code"],
      key: "location",
      width: 100,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
    },
  ];

  const handleAddToExport = (record: any) => {
    if (
      selectedItems.length > 0 &&
      record.project !== selectedItems[0].project
    ) {
      notify.error(
        "Không thể thêm mã hàng",
        "Chỉ có thể chọn các mã hàng cùng dự án.",
      );
      return;
    }

    if (!selectedItems.find((item) => item.id === record.id)) {
      setSelectedItems([...selectedItems, { ...record, exportQuantity: 1 }]);
    }
  };

  const handleRemoveFromExport = (recordId: number) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== recordId));
  };

  const handleQuantityChange = (recordId: number, quantity: number) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.id === recordId
          ? { ...item, exportQuantity: quantity || 0 }
          : item,
      ),
    );
  };

  const updatedColumns = columns.map((col) => {
    if (col.key === "action") {
      return {
        ...col,
        render: (_: any, record: any) => (
          <Button
            icon={<ExportOutlined />}
            onClick={() => handleAddToExport(record)}
            disabled={
              record.qcStatus === "PENDING" ||
              record.qcStatus === "REJECT" ||
              record.stock <= 0 ||
              selectedItems.some((item) => item.id === record.id) ||
              (selectedItems.length > 0 &&
                record.project !== selectedItems[0].project)
            }
          >
            Chọn
          </Button>
        ),
      };
    }
    return col;
  });

  return (
    <div style={{ maxHeight: "100vh" }}>
      {/* <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Typography.Title
          style={{
            textTransform: "uppercase",
            margin: 0,
            fontFamily: "Chakra Petch",
          }}
          level={3}
          className="dark:text-white"
        >
          Xuất kho
        </Typography.Title>
      </div> */}

      <div className="flex gap-4 flex-wrap lg:flex-nowrap">
        <div className="flex-1 w-full lg:min-w-[600px]">
          <Card size="small" className="mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              {/* <Input
                placeholder="Tìm kiếm theo Part Number hoặc AWB"
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: "100%", maxWidth: 400, minWidth: 300 }}
                prefix={<SearchOutlined />}
              /> */}
              <Space wrap>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const headers = [
                      "Project",
                      "Mã dự án",
                      "Part Number",
                      "AWB",
                      "Mã kho",
                      "Location",
                      "Số lượng xuất kho",
                    ];
                    const exampleRow = {
                      Project: "Example: PJ001",
                      "Mã dự án": "Example: PJ001",
                      "Part Number": "Example: ABC123",
                      AWB: "Example: AWB001",
                      "Mã kho": "Example: TK2",
                      Location: "Example: TK2-01",
                      "Số lượng xuất kho": "Example: 10",
                    };
                    const ws = XLSX.utils.json_to_sheet([exampleRow], {
                      header: headers,
                    });
                    ws["!cols"] = headers.map(() => ({ wch: 20 }));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Xuất kho");
                    XLSX.writeFile(wb, "xuat_kho_template.xlsx");
                  }}
                >
                  Tải mẫu Excel
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={handleExcelUpload}
                  disabled={importMutation.isPending}
                >
                  <Button
                    loading={importMutation.isPending}
                    icon={<UploadOutlined />}
                  >
                    Nhập từ Excel
                  </Button>
                </Upload>
              </Space>
              <Popover
                title="Lọc theo mã kho"
                trigger="click"
                content={
                  <div style={{ padding: "8px" }}>
                    <Space direction="vertical">
                      <Space wrap>
                        {filterOptions.map((option) => (
                          <Button
                            key={option.value}
                            type={
                              warehouseFilter === option.value
                                ? "primary"
                                : "default"
                            }
                            onClick={() => {
                              setWarehouseFilter(
                                warehouseFilter === option.value
                                  ? undefined
                                  : option.value,
                              );
                              setCurrentPage(1);
                            }}
                            size="small"
                          >
                            {option.label}
                          </Button>
                        ))}
                      </Space>
                      {warehouseFilter && (
                        <Button
                          type="link"
                          danger
                          size="small"
                          onClick={() => {
                            setWarehouseFilter(undefined);
                            setCurrentPage(1);
                          }}
                        >
                          Xóa bộ lọc
                        </Button>
                      )}
                    </Space>
                  </div>
                }
              >
                <Button type={warehouseFilter ? "primary" : "default"}>
                  Mã kho {warehouseFilter ? `(${warehouseFilter})` : ""}
                </Button>
              </Popover>
            </div>
          </Card>

          <Table
            columns={updatedColumns}
            dataSource={warehouseData?.data || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: warehouseData?.pagination?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ x: 1200, y: 600 }}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              width: "100%",
            }}
          />
        </div>

        <Card
          title="Danh sách xuất kho"
          className="w-full lg:w-96 lg:max-w-md"
          extra={
            <Button
              type="primary"
              disabled={selectedItems.length === 0}
              onClick={handlePreview}
            >
              <ExportOutlined />
              Preview
            </Button>
          }
        >
          <div className="space-y-4">
            {selectedItems.map((item) => (
              <Card
                key={item.id}
                size="small"
                className="bg-[var(--bg-secondary)]"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium">PN : {item.partNumber}</div>
                    <div className="font-medium">AWB : {item.awb}</div>
                    <div className="text-sm text-gray-500">
                      PJ : {item.projectCode}
                    </div>
                  </div>
                  <Button
                    size="small"
                    danger
                    onClick={() => handleRemoveFromExport(item.id)}
                  >
                    Xóa
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm">Số lượng xuất:</span>
                  <InputNumber
                    min={1}
                    max={item.stock}
                    value={item.exportQuantity}
                    onChange={(value) =>
                      handleQuantityChange(item.id, value || 0)
                    }
                    style={{ width: 100 }}
                  />
                  <span className="text-sm text-gray-500">
                    / {item.stock} {item.unit}
                  </span>
                </div>
              </Card>
            ))}
            {selectedItems.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                Chưa có mã hàng nào được chọn
              </div>
            )}
          </div>
        </Card>
      </div>
      <ModalPreviewExport
        open={previewVisible}
        selectedItems={selectedItems}
        onOk={handleExportSubmit}
        onCancel={() => setPreviewVisible(false)}
      />
    </div>
  );
};
export default WarehouseExport;
